"use client"

import { supabase } from "@/lib/supabase/browser-client"
import { FC, useEffect, useState } from "react"
import { getCheckoutCustomer } from "@/lib/stripe/server"
import { Tables } from "@/supabase/types"
import { logError } from "@/utils/errorLogging"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('welcome.welcomeSubscriber', supabase)
interface WelcomeSubscriberProps {
  sessionId: string
}

export const WelcomeSubscriber: FC<WelcomeSubscriberProps> = ({
  sessionId
}) => {
	WelcomeSubscriber.displayName = 'SidebarContent'

	const [isNewUser, setNewUser] = useState<boolean>(false)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [profile, setProfile] = useState<Partial<Tables<"profiles">> | null>(null)
  const [checkingStatus, setCheckingStatus] = useState<boolean>(true)

  useEffect(() => {
    const init = async (retryCount = 0) => {
			const { customer, error, retry } = await getCheckoutCustomer(sessionId)
			if (error) {
				logger.error('Error:')
				setErrorMessage(error)
				setCheckingStatus(false)
			} else if (customer) {
				setNewUser(true)
				setCheckingStatus(false)
				if (!customer.auth_id) {
					logError(logger, error, `Customer authId not found for ${customer.id}`)
					return
				}

				const response = await fetch(`/api/stripe/signup?action=getProfile&userId=${customer.auth_id}`)
				if (response.ok) {
					const data = await response.json()
					setProfile(data)
				}
			} else if (retry && retryCount < 5) {
				setTimeout(() => init(retryCount + 1), 500)
			} else {
				setErrorMessage("Unable to confirm subscription after multiple attempts")
				setCheckingStatus(false)
			}
		}

		init().catch(error => {
		logError(logger, error, "Error during init:")
	  })
  }, [sessionId])

  return (
		<>
			<h1 className="mb-6 text-center text-3xl font-bold">Welcome to Aidon AI</h1>
			{checkingStatus && (
				<div>
					<h3 className="mb-6 text-center text-xl">Thank you for subscribing! We&apos;re finalizing your subscription. This should only take a moment.</h3>
					<p className="text-center text-lg">Your journey with Aidon AI is about to begin.</p>
				</div>
			)}
			{isNewUser && !checkingStatus && (
				<div>
					<h3 className="mb-6 text-center text-xl">{`Congratulations, ${profile?.display_name ?? profile?.username ?? 'valued subscriber'}! Your subscription is now active.`}</h3>
					<p className="text-center text-lg">Get ready to explore the full potential of Aidon AI!</p>
				</div>
			)}
			{errorMessage && !checkingStatus && (
				<div>
				<h3 className="text-bg-red-600 mb-6 text-center text-xl">{`An error has occurred: ${errorMessage}.`}</h3>
					<p className="text-center text-lg">Please contact our support team for assistance.</p>
				</div>
			)}
		</>
  )
}